import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useFetch } from '@/modules/core/composables/useFetch'

///////////////////////////////////////////////////////////////////////////////
// Store
///////////////////////////////////////////////////////////////////////////////

export type Folder = {
  id: string
  name: string
  parent_id: string
  assigned_shareholders: {
    id: string
    name: string
    type: string
  }[]
  fileable_type: string
  fileable_id: string
  access_level: 'site_admin' | 'group_admin' | 'admin' | 'investor'
  updated_at: string
  created_at: string
  system_folder: 'investors' | 'entity' | 'group' | 'investor' | 'closing_documents' | 'exit_documents'
}

export type File = {
  id: string
  name: string
  filing_folder_id: string
  file_url: string
  file_type: string
  access_level: 'site_admin' | 'group_admin' | 'admin' | 'investor'
  assigned_shareholders: {
    id: string
    name: string
    type: string
  }[]
  updated_at: string
  created_at: string
}

export const useFilingStore = defineStore('filingStore', () => {
  const route = useRoute()
  const baseUrl = computed(() => `/v4/${route.params.slug}/filing`)

  const entityId = computed(() => route.params.entity_id)

  // Folders and Files
  const folderItems = ref<Folder[]>([])
  const fileItems = ref<File[]>([])
  const searchResultItems = ref<{ folders: Folder[]; files: File[] }>({ folders: [], files: [] })
  const rootFolderItem = ref<Folder | null>(null)

  const folders = computed(() => folderItems.value)
  const files = computed(() => fileItems.value)
  const searchResults = computed(() => searchResultItems.value)
  const rootFolder = computed(() => rootFolderItem.value)

  const resetStore = () => {
    folderItems.value = []
    fileItems.value = []
  }

  const fetchFolders = async ({
    parent_id,
    individual_id,
    other_entity_id,
    shareholder_cids,
  }: {
    parent_id?: string
    individual_id?: string
    other_entity_id?: string
    shareholder_cids?: string[]
  }) => {
    const params = new URLSearchParams()
    if (parent_id) params.append('parent_id', parent_id)
    if (individual_id) params.append('individual_id', individual_id)
    if (other_entity_id) params.append('other_entity_id', other_entity_id)
    if (shareholder_cids) params.append('shareholder_cids', shareholder_cids.join(','))

    const queryParams = params.toString() ? `?${params}` : ''

    const data = await useFetch(`${baseUrl.value}/folders${queryParams}`).get<Folder[]>()

    folderItems.value = folderItems.value.filter((folder) => !data.some((newFolder) => newFolder.id === folder.id))
    folderItems.value = [...folderItems.value, ...data]
  }

  const fetchFolder = async (folder_id: string) => {
    const data = await useFetch(`${baseUrl.value}/folders/${folder_id}`).get<Folder>()

    folderItems.value = folderItems.value.filter((folder) => folder.id.toString() !== folder_id.toString())
    folderItems.value = [...folderItems.value, data]
  }

  const fetchFiles = async (folderId: string = null) => {
    const params = new URLSearchParams()
    if (folderId !== null) params.append('folder_id', folderId)

    const url = `${baseUrl.value}/files?${params.toString()}`
    const data = await useFetch(url).get<File[]>()

    fileItems.value.push(...data.filter((file) => !fileItems.value.some((existingFile) => existingFile.id === file.id)))
  }

  const addFolder = async (payload: object) => {
    await useFetch(`${baseUrl.value}/folders`).post(payload)
  }

  const addFile = async (file: File, folderId?: string, fileableId?: number, fileableType?: number) => {
    try {
      const formData = new FormData()
      formData.append('file[file]', file)
      formData.append('file[name]', file.name)
      formData.append('file[fileable_id]', fileableId?.toString())
      formData.append('file[fileable_type]', fileableType?.toString())
      if (folderId) {
        formData.append('file[filing_folder_id]', folderId)
      }
      await useFetch(`${baseUrl.value}/files`).post(formData)
      fetchFiles(folderId)
    } catch (error) {
      console.error('Error fetching file from backend:', error)
      throw error
    }
  }

  const removeFile = async (id: string) => {
    await useFetch(`${baseUrl.value}/files/${id}/remove`).post()
    fileItems.value = fileItems.value.filter((file) => file.id !== id)
  }

  const removeFolder = async (folder_id: string) => {
    await useFetch(`${baseUrl.value}/folders/${folder_id}/remove`).post()
    folderItems.value = folderItems.value.filter((folder) => folder.id !== folder_id)
  }

  const downloadFile = async (fileId) => {
    try {
      const response = await useFetch(`${baseUrl.value}/files/${fileId}/download`).download()

      return response
    } catch (error) {
      console.error('Error fetching file from backend:', error)
      throw error
    }
  }

  const downloadFolder = async (folderId) => {
    try {
      const response = await useFetch(`${baseUrl.value}/folders/${folderId}/download`).download()
      return response
    } catch (error) {
      console.error('Error fetching folder from backend:', error)
      throw error
    }
  }

  const moveFile = async (fileId: string, newFolderId: string) => {
    const payload = { file: { filing_folder_id: newFolderId } }

    await useFetch(`${baseUrl.value}/files/${fileId}/move`).post(payload)
    const file = fileItems.value.find((file) => file.id === fileId)
    if (file) {
      file.filing_folder_id = newFolderId
      fileItems.value = [...fileItems.value]
    }
  }

  const moveFolder = async (folderId: string, newParentId: string | null) => {
    const payload = { folder: { parent_id: newParentId } }

    await useFetch(`${baseUrl.value}/folders/${folderId}/update`).post(payload)

    const folder = folderItems.value.find((folder) => folder.id === folderId)
    if (folder) {
      folder.parent_id = newParentId
      fileItems.value = [...fileItems.value]
    }
  }

  const downloadAll = async (parentId: string = null, individual_id: string, other_entity_id: string) => {
    const params = new URLSearchParams()
    if (parentId) params.append('parent_id', parentId)
    if (individual_id) params.append('individual_id', individual_id)
    if (other_entity_id) params.append('other_entity_id', other_entity_id)

    try {
      const url = `${baseUrl.value}/folders/download_all?${params.toString()}`

      const response = await useFetch(url).download()
      return response
    } catch (error) {
      console.error('Error downloading all files and folders:', error)
      throw error
    }
  }

  const updateFolder = async (folderId: string, payload: Object) => {
    await useFetch(`${baseUrl.value}/folders/${folderId}/update`).post(payload)
  }

  const search = async (
    query: string,
    {
      parent_id,
      individual_id,
      other_entity_id,
    }: { parent_id?: string; individual_id?: string; other_entity_id?: string } = {},
  ) => {
    try {
      const params = new URLSearchParams({ q: query })
      if (parent_id) params.append('parent_id', parent_id)
      if (individual_id) params.append('individual_id', individual_id)
      if (other_entity_id) params.append('other_entity_id', other_entity_id)
      const queryParams = params.toString() ? `?${params}` : ''

      const response = await useFetch(`${baseUrl.value}/folders/search${queryParams}`).get<{
        folders: Folder[]
        files: File[]
      }>()
      searchResultItems.value = response
    } catch (error) {
      console.error('Search failed:', error)
    }
  }

  // Helpers
  const accessLevelLabels = (folder: Folder) => {
    if (!folder?.access_level) return null

    switch (folder.access_level) {
      case 'site_admin':
        return 'Site Admins'
      case 'group_admin':
        return 'Organization Admins'
      case 'investor':
        if (folder.fileable_type === 'Investor') return 'Investor'
        return 'Investors'
      case 'admin':
        if (folder.fileable_type === 'InvestorSet') return 'Entity Admins'
        if (folder.fileable_type === 'Investor') return 'Investor Admins'
    }

    return folder.access_level
  }

  const updateAccessLevelOfDescendants = async (parentFolderId: string) => {
    const parentFolder = folders.value.find((folder) => folder.id === parentFolderId)
    const descendantFolders = folders.value.filter((folder) => folder.parent_id === parentFolderId)
    const descendantFiles = files.value.filter((file) => file.filing_folder_id === parentFolderId)

    descendantFolders.forEach((descendantFolder) => {
      descendantFolder.access_level = parentFolder.access_level
      descendantFolder.assigned_shareholders = [...parentFolder.assigned_shareholders]
      updateAccessLevelOfDescendants(descendantFolder.id)
    })

    descendantFiles.forEach((descendantFile) => {
      descendantFile.access_level = parentFolder.access_level
      descendantFile.assigned_shareholders = [...parentFolder.assigned_shareholders]
    })
  }

  return {
    folders,
    files,
    searchResults,
    rootFolder,
    resetStore,
    fetchFolders,
    fetchFolder,
    fetchFiles,
    moveFile,
    addFolder,
    addFile,
    downloadFile,
    downloadFolder,
    removeFolder,
    removeFile,
    moveFolder,
    downloadAll,
    updateFolder,
    search,

    accessLevelLabels,
    updateAccessLevelOfDescendants,
  }
})
